import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgbModule, NgbDateAdapter, NgbDateParserFormatter, NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { DragulaModule } from 'ng2-dragula';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastrModule, ToastContainerModule } from 'ngx-toastr';
import { TagInputModule } from 'ngx-chips';
import { ApiModule } from '../api/api.module';
import { NgxSpinnerModule } from "ngx-spinner";


import { AppComponent } from './app.component';
import { AppRoutingModule } from './routing.module';
import { HeaderComponent } from './header/header.component';
import { HeaderService } from './header/header.service';
import { MarketBannerComponent } from './market-banner/market-banner.component';
import { NavigationPanelComponent } from './navigation-panel/navigation-panel.component';
import { NavigationButtonComponent } from './navigation-button/navigation-button.component';
import { FooterComponent } from './footer/footer.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { ProfileComponent } from './profile/profile.component';
import { SearchComponent } from './search/search.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { DashboardMyOasisComponent } from './dashboard-my-oasis/dashboard-my-oasis.component';
import { DashboardMarketsComponent } from './dashboard-markets/dashboard-markets.component';
import { DashboardOrdersComponent } from './dashboard-orders/dashboard-orders.component';
import { DashboardReportsComponent } from './dashboard-reports/dashboard-reports.component';
import { ManagementComponent } from './management/management.component';
import { NotificationsComponent } from './notifications/notifications.component';
import { FileUploadComponent } from './file-upload/file-upload.component';
import { AuthShellComponent } from './auth-shell/auth-shell.component';

import { ThemeColorPickerComponent } from './theme-color-picker/theme-color-picker.component';
import { InitThemeDirective } from './init-theme.directive';
import { ClientEditComponent } from './management/client-edit/client-edit.component';
import { ClientListComponent } from './management/client-list/client-list.component';
import { ApprovalsViewsComponent } from './approvals-views/approvals-views.component';
import { FocusDirective } from './focus.directive';
import { SpinnerComponent } from './spinner/spinner.component';

import { ProductAliasComponent } from './product-alias/product-alias.component';
import { ProductSetupRequestAddComponent } from './product-alias/product-setup-request-add/product-setup-request-add.component';

import { MarketEditComponent } from './market-edit/market-edit.component';
import { PreviewModalComponent } from './management/client-edit/preview-modal/preview-modal.component';
import { DashboardMyOasisDetailComponent } from './dashboard-my-oasis/dashboard-my-oasis-detail/dashboard-my-oasis-detail.component';
import { ColorBarComponent } from './dashboard-my-oasis/dashboard-my-oasis-detail/color-bar/color-bar.component';
import { IconPickerComponent } from './icon-picker/icon-picker.component';
import { LoginComponent } from './login/login.component';

import { AuthGuardService } from './services/auth-guard.service';
import { AdminGuardService } from './services/admin-guard.service';

import { TokenInterceptor } from './interceptors/token.interceptor';
import { ForbiddenInterceptor } from './interceptors/forbidden.interceptor';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';

import { ClientEditService } from './management/client-edit/client-edit.service';
import { OrderTableComponent } from './order-table/order-table.component';
import { DashboardButtonsService } from './services/dashboard-buttons.service';

import { NgbDateNativeAdapter } from './shared/ngb-date/adapter';
import { NgbDateENUSParserFormatter } from './shared/ngb-date/enus-parser-formatter';
import { UserListComponent } from './management/user-list/user-list.component';
import { UserEditComponent } from './management/user-edit/user-edit.component';
import { ChipsComponent } from './chips/chips.component';
import { DeleteModalComponent } from './delete-modal/delete-modal.component';
import { ProjectCreationComponent } from './project-creation/project-creation.component';
import { ThemeService } from './services/theme.service';
import { MembersListComponent } from './management/members-list/members-list.component';
import { TeamEditComponent } from './management/team-edit/team-edit.component';
import { OrderConfirmationComponent } from './order-confirmation/order-confirmation.component';
import { ApproveOrderComponent } from './approve-order/approve-order.component';
import { OrderWorksheetComponent } from './order-worksheet/order-worksheet.component';
import { OrderCreateComponent } from './order-create/order-create.component';
import { TeamListComponent } from './management/team-list/team-list.component';
import { OrderService } from './services/order.service';
import { ClientLogoService } from './services/client-logo.service';
import { AvatarComponent } from './avatar/avatar.component';
import { AvatarService } from './services/avatar.service';
import { MarketService } from '../api/services/market.service';
import { WorksheetComponent } from './order-worksheet/worksheet/worksheet.component';
import { BomsComponent } from './order-worksheet/boms/boms.component';
import { BomListComponent } from './boms/bom-list/bom-list.component';
import { BomEditComponent } from './boms/bom-edit/bom-edit.component';
import { OrderApprovalComponent } from './order-approval/order-approval.component';
import { OrderListComponent } from './dashboard-orders/order-list/order-list.component';
import { OrderHubComponent } from './dashboard-orders/order-hub/order-hub.component';
import { AddressCreationComponent } from './address-creation/address-creation.component';
import { ProductDetailComponent } from './product-detail/product-detail.component';
import { ReportCreateComponent } from './report-create/report-create.component';
import { ApprovalsComponent } from './approvals/approvals.component';
import { CommentsComponent } from './comments/comments.component';
import { MarketBannerService } from './market-banner/market-banner.service';
import { SearchService } from '../api/services/search.service';
import { FileDownloadComponent } from './file-download/file-download.component';
import { DelegationsComponent } from './management/delegations/delegations.component';
import { CommentComponent } from './comments/comment/comment.component';
import { CommentService } from '../api/services/comment.service';
import { UserNameComponent } from './comments/user-name/user-name.component';
import { CommenterRoleComponent } from './comments/commenter-role/commenter-role.component';
import { CommenterActionComponent } from './comments/commenter-action/commenter-action.component';
import { FilesModalComponent } from './comments/files-modal/files-modal.component';
import { ApproverReasonComponent } from './comments/approver-reason/approver-reason.component';
import { ProjectHealthComponent } from './dashboard-my-oasis/project-health/project-health.component';

import { ProductAdvisoriesComponent } from './product-advisories/product-advisories.component';
import { ReadonlyWorksheetComponent } from './readonly-worksheet/readonly-worksheet.component';
import { ReadMoreComponent } from './read-more/read-more.component';
import { RenamableComponent } from './renamable/renamable.component';
import { RenameModalComponent } from './renamable/rename-modal/rename-modal.component';
import { ProjectNameComponent } from './approvals/project-name/project-name.component';
import { NotificationsPreferencesComponent } from './notifications-preferences/notifications-preferences.component';
import { CutCableModalComponent } from './cut-cable-modal/cut-cable-modal.component';
import { PermissionWrapperComponent } from './permission-wrapper/permission-wrapper.component';
import { BomCommentComponent } from './bom-table/bom-comment/bom-comment.component';
import { PreviewImageComponent } from './preview-image/preview-image.component';
import { FileDescriptionComponent } from './file-description/file-description.component';
import { DownloadCopyComponent } from './order-worksheet/download-copy/download-copy.component';
import { ChangePasswordComponent } from './change-password/change-password.component';
import { NewTeamComponent } from './management/new-team/new-team.component';
import { ImportDataComponent } from './order-worksheet/import-data/import-data.component';
import { WarrantyClaimComponent } from './warranty-claim/warranty-claim.component';
import { ProjectSpendingSummaryComponent } from './dashboard-my-oasis/project-spending-summary/project-spending-summary.component';
import { CheckoutService } from './checkout/checkout.service';
import { BomTableService } from './bom-table/bom-table.service';
import { BomCreateComponent } from './boms/bom-create/bom-create.component';
import { TasksFilterComponent } from './dashboard-my-oasis/dashboard-my-oasis-detail/tasks-filter/tasks-filter.component';
import { ProjectOrderSummaryComponent } from './dashboard-my-oasis/project-order-summary/project-order-summary.component';
import { TaskConfirmationDialogComponent } from './dashboard-my-oasis/project-order-summary/task-confirmation-dialog/task-confirmation-dialog.component';
import { TasksIconsComponent } from './dashboard-my-oasis/project-order-summary/tasks-icons/tasks-icons.component';
import { CutCableTableComponent } from './cut-cable-modal/cut-cable-table/cut-cable-table.component';
import { OrderDetailsComponent } from './order-details/order-details.component';
import { FullPageWrapperComponent } from './full-page-wrapper/full-page-wrapper.component';
import { RogConfirmationDialogComponent } from './rog-confirmation-dialog/rog-confirmation-dialog.component';
import { RogLinesTableComponent } from './rog-confirmation-dialog/rog-lines-table/rog-lines-table.component';
import { LoadingEllipsisComponent } from './loading-ellipsis/loading-ellipsis.component';
import { ImportExportComponent } from './order-worksheet/import-export/import-export.component';
import { BomCsvUploadErrorComponent } from './bom-table/bom-csv-upload-error/bom-csv-upload-error.component';
import { ProductAdvisoriesDetailsComponent } from './product-advisories/product-advisories-details/product-advisories-details.component';
import { WorksheetResultComponent } from './search/worksheet-result/worksheet-result.component';
import { OrderDetailViewComponent } from './order-detail-view/order-detail-view.component';
import { MarketOrderSummaryComponent } from './dashboard-my-oasis/market-order-summary/market-order-summary.component';
import { OrderActivityLogComponent } from './order-activity-log/order-activity-log.component';
import { ConfirmationModalComponent } from './confirmation-modal/confirmation-modal.component';
import { DashboardTiledMyOasisComponent } from './dashboard-my-oasis/dashboard-tiled-my-oasis/dashboard-tiled-my-oasis.component';

import { BillToAddressSort } from './market-edit/bill-to-sort.pipe';
import { ShipViaOptionsSort } from './checkout/sort-ship-via-options.pipe';
import { MessageComponent } from './message/message.component';
import { DisplayMessageModalComponent } from './display-message-modal/display-message-modal.component';
import { RogReportImagesComponent } from './rog-report-images/rog-report-images.component';

import { TasksComponent } from './tasks/tasks.component';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { InfoModalComponent } from './info-modal/info-modal.component';
import { RetractModalComponent } from './retract-modal/retract-modal.component';
import { DatePipe } from '@angular/common';
import { OrderAddressChangeModalComponent } from './order-address-change-modal/order-address-change-modal.component';
import { BpaUploadComponent } from './bpa-upload/bpa-upload.component';
import { BulkProjectUploadComponent } from './bulk-project-upload/bulk-project-upload.component';
import { StripSpecialCharsDirective } from './strip-special-chars.directive';
import { CheckoutComponent } from './checkout/checkout.component';
import { BomTableComponent } from './bom-table/bom-table.component';
import { PartialStockComponent } from './bom-table/partial-stock/partial-stock.component';
import { BlockCopyPasteDirective } from './BlockCopyPasteDirective.directive';
import { StripSpecialCharsWithSpaceDirective } from './strip-special-chars-with-space.directive';

import { MarketListComponent } from './management/market-list/market-list.component';
import { OrderStatusReportComponent } from './dashboard-reports/orderstatus-report/orderstatus-report.component';
import { WorksheetStatusReportComponent } from './dashboard-reports/worksheetstatus-report/worksheetstatus-report.component';
import { TaskStatusReportComponent } from './dashboard-reports/taskstatus-report/taskstatus-report.component';
import { AnalyticsService } from '../api/services/analytics.service';
import { Router, NavigationEnd } from '@angular/router';
import { EnhancementFeedbackComponent } from './enhancement-feedback/enhancement-feedback.component';
@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    MarketBannerComponent,
    NavigationPanelComponent,
    NavigationButtonComponent,
    FooterComponent,
    DashboardComponent,
    ProfileComponent,
    SearchComponent,
    PageNotFoundComponent,
    DashboardMyOasisComponent,
    DashboardTiledMyOasisComponent,
    DashboardMarketsComponent,
    DashboardOrdersComponent,
    DashboardReportsComponent,
    ManagementComponent,
    NotificationsComponent,
    FileUploadComponent,
    ThemeColorPickerComponent,
    InitThemeDirective,
    ClientEditComponent,
    ClientListComponent,
    ApprovalsViewsComponent,
    FocusDirective,
    ProductAliasComponent,
    ProductSetupRequestAddComponent,
    MarketEditComponent,
    PreviewModalComponent,
    DashboardMyOasisDetailComponent,
    ColorBarComponent,
    IconPickerComponent,
    LoginComponent,
    ResetPasswordComponent,
    ForgotPasswordComponent,
    AuthShellComponent,
    SpinnerComponent,
    OrderTableComponent,
    UserListComponent,
    UserEditComponent,
    ChipsComponent,
    DeleteModalComponent,
    ProjectCreationComponent,
    MembersListComponent,
    TeamEditComponent,
    OrderConfirmationComponent,
    ApproveOrderComponent,
    OrderWorksheetComponent,
    OrderCreateComponent,
    TeamListComponent,
    AvatarComponent,
    WorksheetComponent,
    BomsComponent,
    BomListComponent,
    BomEditComponent,
    OrderApprovalComponent,
    OrderListComponent,
    OrderHubComponent,
    AddressCreationComponent,
    ProductDetailComponent,
    ReportCreateComponent,
    ApprovalsComponent,
    FileDownloadComponent,
    DelegationsComponent,
    CommentsComponent,
    CommentComponent,
    UserNameComponent,
    CommenterRoleComponent,
    CommenterActionComponent,
    FilesModalComponent,
    ApproverReasonComponent,
    ProjectHealthComponent,
    ProductAdvisoriesComponent,
    ProductAdvisoriesDetailsComponent,
    ReadonlyWorksheetComponent,
    ReadMoreComponent,
    RenamableComponent,
    RenameModalComponent,
    ProjectNameComponent,
    NotificationsPreferencesComponent,
    CutCableModalComponent,
    PermissionWrapperComponent,
    BomCommentComponent,
    PreviewImageComponent,
    FileDescriptionComponent,
    DownloadCopyComponent,
    ChangePasswordComponent,
    NewTeamComponent,
    ImportDataComponent,
    WarrantyClaimComponent,
    ProjectSpendingSummaryComponent,
    BomCreateComponent,
    TasksFilterComponent,
    ProjectOrderSummaryComponent,
    TaskConfirmationDialogComponent,
    TasksIconsComponent,
    CutCableTableComponent,
    OrderDetailsComponent,
    FullPageWrapperComponent,
    RogConfirmationDialogComponent,
    RogLinesTableComponent,
    LoadingEllipsisComponent,
    ImportExportComponent,
    BomCsvUploadErrorComponent,
    WorksheetResultComponent,
    OrderDetailViewComponent,
    MarketOrderSummaryComponent,
    OrderActivityLogComponent,
    ConfirmationModalComponent,
    BomTableComponent,
    PartialStockComponent,
    CheckoutComponent,
    BillToAddressSort,
    ShipViaOptionsSort,
    MessageComponent,
    TasksComponent,
    RetractModalComponent,
    InfoModalComponent,
    DisplayMessageModalComponent,
    OrderAddressChangeModalComponent,
    BpaUploadComponent,
    RogReportImagesComponent,
    BulkProjectUploadComponent,
    StripSpecialCharsDirective,
    StripSpecialCharsWithSpaceDirective,
    BlockCopyPasteDirective,
    MarketListComponent,
    OrderStatusReportComponent,
    WorksheetStatusReportComponent,
    TaskStatusReportComponent,
    EnhancementFeedbackComponent
  ],
  entryComponents: [
    ProductSetupRequestAddComponent,
    MarketEditComponent,
    PreviewModalComponent,
    IconPickerComponent,
    DeleteModalComponent,
    ProjectCreationComponent,
    AddressCreationComponent,
    ReportCreateComponent,
    FilesModalComponent,
    ApproverReasonComponent,
    RenameModalComponent,
    NotificationsPreferencesComponent,
    CutCableModalComponent,
    PreviewImageComponent,
    DownloadCopyComponent,
    ChangePasswordComponent,
    NewTeamComponent,
    ImportDataComponent,
    BomCreateComponent,
    TaskConfirmationDialogComponent,
    RogConfirmationDialogComponent,
    OrderCreateComponent,
    ImportExportComponent,
    BomCsvUploadErrorComponent,
    ConfirmationModalComponent,
    RetractModalComponent,
    InfoModalComponent,
    DisplayMessageModalComponent,
    OrderAddressChangeModalComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ApiModule,
    FormsModule,
    ReactiveFormsModule,
    NgbModule.forRoot(),
    NgMultiSelectDropDownModule.forRoot(),
    DragulaModule,
    ToastrModule.forRoot({ maxOpened: 1 }),
    TagInputModule,
    ToastContainerModule,
    NgxSpinnerModule,
    AppRoutingModule // note: keep this module last so other routes are loaded
  ],
  providers: [
    CheckoutService,
    AuthGuardService,
    AdminGuardService,
    HeaderService,
    ClientEditService,
    DashboardButtonsService,
    MarketService,
    ThemeService,
    OrderService,
    ClientLogoService,
    AvatarService,
    MarketBannerService,
    SearchService,
    CommentService,
    CheckoutService,
    BomTableService,
    DatePipe,
    AnalyticsService,
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ForbiddenInterceptor, multi: true },
    { provide: NgbDateAdapter, useClass: NgbDateNativeAdapter },
    { provide: NgbDropdown, useClass: NgbDropdown },
    { provide: NgbDateParserFormatter, useClass: NgbDateENUSParserFormatter },
  ],
  bootstrap: [AppComponent]
})
export class AppModule { 
  constructor(router: Router, analytics: AnalyticsService) {
    // Logic to track page views
    this.sendDataToAnalytics(router, analytics);
  }

  private sendDataToAnalytics(router: Router, analytics: AnalyticsService): void {
    router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        analytics.trackPageView(event.urlAfterRedirects);
      }
    });
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src3408204168/src/bitbucket.org/tesscotds/webclient/src/app/app.module.ts